import type { Ref } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { TooltipProps } from 'recharts';
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    LabelList,
} from 'recharts';
import type { ChartReportProps } from 'c-reports/Types';
import { ChartDataKeyDateFormat, generateBrush } from 'c-reports/Generators';
import { useTheme } from '@mui/styles';
import { isValid, parse } from 'date-fns';
import { useDateUtils } from 'c-hooks';
import { formatCompactLower } from 'c-lib';
import { Box } from '@mui/material';
import PannableSlider from 'c-components/PannableSlider/PannableSlider';
import { buildPercentYAxisDomain, buildYAxisDomain } from 'c-reports/yAxisDomain';

type Props = Omit<ChartReportProps<any>, 'onDataUpdated'> & {
    showValueLabels?: boolean;
    valueLabelType?: 'number' | 'percent';
    percentDataKey?: string;
    valuesArePercent?: boolean;
};

const norm = (s: string) =>
    String(s ?? '')
        .toLowerCase()
        .replace(/\s+/g, '');

const formatSecondsAsHrsMins = (seconds: number): string => {
    if (!Number.isFinite(seconds) || seconds <= 0) return '0 hrs';
    const totalMinutes = Math.floor(seconds / 60);
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;

    if (hrs === 0) return `${mins} mins`;
    if (mins === 0) return `${hrs.toLocaleString()} hrs`;
    return `${hrs.toLocaleString()} hrs ${mins} mins`;
};

const formatPercentLabel = (value: number): string => {
    if (value === Number.POSITIVE_INFINITY) return '∞%';
    if (!Number.isFinite(value)) return '0%';
    if (Math.abs(value) > 0 && Math.abs(value) < 0.5) return `${value.toFixed(2)}%`;
    if (Math.abs(value) >= 1000) return `${formatCompactLower(value)}%`;
    return `${Math.round(value)}%`;
};

const formatPercentAxisLabel = (value: number): string => {
    if (value === Number.POSITIVE_INFINITY) return '∞%';
    if (!Number.isFinite(value)) return '0%';
    return `${Math.round(value)}%`;
};

const ReportLineChart = (
    {
        data,
        chartProps,
        parts,
        xAxisDataKey,
        yAxisDataKey,
        renderTooltipLabel,
        yAxisTickFormatter,
        hideLegend,
        hideTooltip,
        lineChartProps,
        xAxisProps,
        yAxisProps,
        brushProps,
        hideBrush,
        hideDots,
        yAxisFloor = 'zero',
        chartChildren,
        sliderWrapperProps,
        showValueLabels,
        valueLabelType = 'number',
        percentDataKey,
        valuesArePercent = false,
    }: Props,
    ref: Ref<any>,
) => {
    const [hoveredLegendKey, setHoveredLegendKey] = useState<string | null>(null);

    const onLegendMouseEnter = useCallback<Legend['props']['onMouseEnter']>((payload, _index) => {
        if (payload?.dataKey != null) setHoveredLegendKey(String(payload.dataKey));
    }, []);
    const onLegendMouseLeave = useCallback<Legend['props']['onMouseLeave']>(() => {
        setHoveredLegendKey(null);
    }, []);

    const { accumulative } = useMemo<ChartReportProps<any>['lineChartProps']>(
        () => ({ accumulative: false, ...lineChartProps }),
        [lineChartProps],
    );

    const CustomTooltip = useCallback(
        (tooltipProps: TooltipProps<any, any>) => {
            if (
                renderTooltipLabel &&
                tooltipProps.active &&
                tooltipProps.payload &&
                tooltipProps.payload.length
            ) {
                return renderTooltipLabel(tooltipProps);
            }
            return null;
        },
        [renderTooltipLabel],
    );

    const allPartDataKeys = useMemo(() => parts.map(p => p.dataKey), [parts]);
    const { formatDateString } = useDateUtils();

    const chartData = useMemo(() => {
        let theData = data;
        if (isValid(parse(data?.[0]?.[xAxisDataKey], ChartDataKeyDateFormat, new Date()))) {
            theData = data.map(d => ({
                ...d,
                [xAxisDataKey]: formatDateString({
                    date: parse(d[xAxisDataKey], ChartDataKeyDateFormat, new Date()),
                }),
            }));
        }

        if (accumulative === true && Array.isArray(theData)) {
            const prev: Record<string, number> = {};
            return (theData as any[]).reduce((acc, curr, idx) => {
                const next = { ...curr };
                allPartDataKeys.forEach(k => {
                    const v = Number(curr[k] ?? 0) + (prev[k] ?? 0);
                    next[k] = v;
                    prev[k] = v;
                });
                acc[idx] = next;
                return acc;
            }, [] as any[]);
        }

        return theData;
    }, [data, xAxisDataKey, accumulative, formatDateString, allPartDataKeys]);

    const tickFormatter = useCallback(
        (value: any, index: number) => {
            if (valuesArePercent) {
                const n = Number(value);
                return Number.isFinite(n) ? formatPercentAxisLabel(n) : value;
            }
            if (typeof yAxisTickFormatter === 'function') return yAxisTickFormatter(value, index);
            const n = Number(value);
            return Number.isFinite(n) ? formatCompactLower(n) : value;
        },
        [valuesArePercent, yAxisTickFormatter],
    );

    const theme = useTheme();
    const [brushRange, setBrushRange] = useState<[number, number]>([
        0,
        Math.max(0, data.length - 1),
    ]);
    useEffect(() => {
        setBrushRange([0, Math.max(0, data.length - 1)]);
    }, [data?.length]);

    const labelDataKeyFor = useCallback((seriesKey: string) => seriesKey, []);

    const isTimeChart = useMemo(() => {
        const first = data?.[0] ?? {};
        return Object.keys(first).some(k => /time played/i.test(k));
    }, [data]);

    const getPercentForSeriesFromRow = useCallback(
        (seriesKey: string, row: any): number => {
            if (!row) return 0;

            const pctField = `${seriesKey}__pct`;
            if (Object.prototype.hasOwnProperty.call(row, pctField)) {
                const v = Number(row[pctField]);
                if (!Number.isNaN(v)) return v;
            }

            if (percentDataKey && Object.prototype.hasOwnProperty.call(row, percentDataKey)) {
                const v = Number(row[percentDataKey]);
                if (!Number.isNaN(v)) return v;
            }

            const metricRoot = seriesKey
                .replace(/^(total|expected|actual|delivered)\s+/i, '')
                .trim();

            const directKeys = [
                `${seriesKey} Percentage`,
                `${seriesKey} %`,
                `${metricRoot} Percentage`,
                `${metricRoot} %`,
            ];
            // eslint-disable-next-line no-restricted-syntax
            for (const key of directKeys) {
                if (Object.prototype.hasOwnProperty.call(row, key)) {
                    const v = Number(row[key]);
                    if (!Number.isNaN(v)) return v;
                }
            }

            const delivered = Number(row[seriesKey]);
            if (Number.isFinite(delivered)) {
                const expectedKey = Object.keys(row).find(
                    k => new RegExp(`^${metricRoot}`, 'i').test(k) && /expected/i.test(k),
                );
                if (expectedKey) {
                    const expected = Number(row[expectedKey]);
                    if (expected > 0) return (delivered / expected) * 100;
                    if (delivered > 0) return Number.POSITIVE_INFINITY;
                }
            }

            const base = norm(metricRoot);
            // eslint-disable-next-line no-restricted-syntax
            for (const key of Object.keys(row)) {
                if (/percentage/i.test(key) && norm(key).includes(base)) {
                    const v = Number(row[key]);
                    if (!Number.isNaN(v)) return v;
                }
            }

            return 0;
        },
        [percentDataKey],
    );
    const MAX_LINE_LABELS = 12;
    const labelStep = Math.max(1, Math.ceil((chartData.length || 1) / MAX_LINE_LABELS));
    const lastLabelIndex = chartData.length - 1;

    const renderSeriesLabel = (seriesKey: string, color?: string) =>
        function SeriesLabel(lp: any) {
            const { x, y, value, index } = lp;
            if (labelStep > 1 && index !== lastLabelIndex) {
                if (index % labelStep !== 0) return null;
                if (lastLabelIndex - index < labelStep) return null;
            }
            const row = chartData[index] ?? data[index] ?? {};

            let text: string;

            if (valueLabelType === 'percent') {
                const pct = getPercentForSeriesFromRow(seriesKey, row);
                text = formatPercentLabel(pct);
            } else {
                const num = Number(value ?? 0);
                if (!Number.isFinite(num) || num === 0) return null;

                if (isTimeChart) {
                    text = formatSecondsAsHrsMins(num);
                } else {
                    text = formatCompactLower(Math.round(num));
                }
            }

            const plotValueFor = (k: string) =>
                valueLabelType === 'percent'
                    ? getPercentForSeriesFromRow(k, row)
                    : Number(row?.[k] ?? 0);
            const thisValue = plotValueFor(seriesKey);
            const isHighestHere = parts.every(p => plotValueFor(String(p.dataKey)) <= thisValue);
            const dy = isHighestHere ? -10 : 14;
            const fill = color || '#444';

            return (
                <g>
                    {/* White halo keeps labels legible over the grid; on a dark background it reads as a border. */}
                    {theme.palette.mode !== 'dark' && (
                        <text
                            x={x}
                            y={y}
                            dy={dy}
                            textAnchor="middle"
                            fill="#fff"
                            stroke="#fff"
                            strokeWidth={3}
                            fontSize={12}
                        >
                            {text}
                        </text>
                    )}
                    <text x={x} y={y} dy={dy} textAnchor="middle" fill={fill} fontSize={12}>
                        {text}
                    </text>
                </g>
            );
        };

    const percentDomain = useMemo(() => {
        if (!valuesArePercent) return undefined;

        const percentValues = chartData.flatMap(row =>
            parts.map(part => getPercentForSeriesFromRow(String(part.dataKey), row)),
        );
        const finiteValues = percentValues.filter(Number.isFinite);

        if (finiteValues.length === 0) return [0, 100] as [number, number];

        const domain = buildPercentYAxisDomain(finiteValues);
        if (!domain) return undefined;
        return [Math.min(0, ...finiteValues), domain[1]] as [number, number];
    }, [chartData, getPercentForSeriesFromRow, parts, valuesArePercent]);

    const numericDomain = useMemo(() => {
        if (valuesArePercent) return undefined;

        const numericValues = chartData.flatMap(row =>
            parts
                .map(part => Number(row?.[String(part.dataKey)]))
                .filter(value => Number.isFinite(value)),
        ) as number[];

        return buildYAxisDomain(numericValues, yAxisFloor);
    }, [chartData, parts, valuesArePercent, yAxisFloor]);

    const effectiveYAxisProps = useMemo(
        () => ({
            ...yAxisProps,
            ...(percentDomain ?? numericDomain ? { domain: percentDomain ?? numericDomain } : {}),
        }),
        [numericDomain, percentDomain, yAxisProps],
    );

    return (
        <>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart
                    ref={ref}
                    data={chartData}
                    margin={{ top: 25, right: 50, left: 40, bottom: 5 }}
                    {...chartProps}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey={xAxisDataKey}
                        {...xAxisProps}
                        tickMargin={10}
                        interval="equidistantPreserveStart"
                        tick={(props: any) => {
                            const { x, y, payload } = props;
                            const text = String(payload?.value ?? '').trim();
                            const maxCharsPerLine = 10;
                            const dateRegex = /^\d{2}-[A-Za-z]{3}-\d{4}$/;

                            let lines: string[] = [];
                            if (dateRegex.test(text)) {
                                const [day, month, year] = text.split('-');
                                lines = [`${day}-${month}`, year];
                            } else {
                                const words = text.split(' ');
                                let current = '';
                                words.forEach(word => {
                                    if ((current + word).length > maxCharsPerLine) {
                                        lines.push(current);
                                        current = word;
                                    } else {
                                        current += (current ? ' ' : '') + word;
                                    }
                                });
                                if (current) lines.push(current);
                            }

                            return (
                                <text x={x} y={y} textAnchor="middle" fontSize={14} fill="#666">
                                    {lines.map((line, idx) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <tspan key={idx} x={x} dy={idx === 0 ? 0 : 16}>
                                            {line}
                                        </tspan>
                                    ))}
                                </text>
                            );
                        }}
                    />
                    <YAxis
                        width={20}
                        dataKey={yAxisDataKey}
                        tickFormatter={tickFormatter}
                        {...effectiveYAxisProps}
                    />

                    {!hideTooltip && (
                        <Tooltip content={renderTooltipLabel ? <CustomTooltip /> : undefined} />
                    )}
                    {!hideLegend && (
                        <Legend
                            verticalAlign="top"
                            align="right"
                            wrapperStyle={{ top: 5 }}
                            onMouseEnter={onLegendMouseEnter}
                            onMouseLeave={onLegendMouseLeave}
                        />
                    )}

                    {parts.map(p => {
                        const key = String(p.dataKey);
                        const strokeColor =
                            (p.lineProps as any)?.stroke ??
                            (p.lineProps as any)?.color ??
                            undefined;

                        return (
                            <Line
                                key={key}
                                type="monotone"
                                dataKey={key}
                                strokeWidth={hoveredLegendKey === key ? 8 : 4}
                                activeDot={{ r: hoveredLegendKey === key ? 16 : 8 }}
                                dot={hideDots ? false : undefined}
                                {...(p.lineProps as any)}
                            >
                                {showValueLabels && (
                                    <LabelList
                                        dataKey={labelDataKeyFor(key)}
                                        content={renderSeriesLabel(key, strokeColor) as any}
                                    />
                                )}
                            </Line>
                        );
                    })}

                    {!hideBrush &&
                        generateBrush({
                            brushProps,
                            startIndex: Math.floor(brushRange[0]),
                            endIndex: Math.ceil(brushRange[1]),
                            dataKey: xAxisDataKey,
                            theme,
                            height: 0,
                        })}

                    {[...(chartChildren ?? [])]}
                </LineChart>
            </ResponsiveContainer>

            {!hideBrush && (
                <Box pl={9} width="95%" mt={-4} {...sliderWrapperProps}>
                    <PannableSlider
                        value={brushRange}
                        onChange={setBrushRange}
                        min={0}
                        max={Math.max(0, data.length - 1)}
                        getLabelForValue={i => data[i]?.[xAxisDataKey] ?? i}
                    />
                </Box>
            )}
        </>
    );
};

export default React.forwardRef(ReportLineChart);
